import React from "react";
import { Link } from "react-router-dom";
import { Container, TextLink } from "@/components/primitives";
import { MarkBtb } from "@/components/BrandLogos";

const productLinks = [
  { label: "CaseMinder", to: "/caseminder" },
  { label: "ComplianceMinder", to: "/complianceminder" },
  { label: "Electra HR", to: "/electra-hr" },
  { label: "Training", to: "/training" },
];

export default function Footer() {
  return (
    <footer className="border-t border-border bg-secondary/40" data-testid="site-footer">
      {/* CTA strip */}
      <div className="border-b border-border">
        <Container className="grid grid-cols-1 gap-8 py-16 md:grid-cols-12 md:items-end">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-4xl">
              Take back control of how your firm runs.
            </h2>
            <p className="mt-4 max-w-xl text-muted-foreground">
              Tired of generic software, fragmented support and compliance that only exists on
              paper? There is now a more credible route.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <TextLink to="/book-a-walkthrough" testid="footer-cta-walkthrough">
              Book a walkthrough
            </TextLink>
          </div>
        </Container>
      </div>

      <Container className="grid grid-cols-1 gap-10 py-14 md:grid-cols-12">
        <div className="md:col-span-4">
          <Link to="/" className="flex items-center gap-3 font-display text-2xl font-medium tracking-tight text-foreground">
            <MarkBtb className="h-8 w-8 shrink-0" strokeWidth={2} />
            Beyond the Box
          </Link>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
            The autonomy stack for law firm owners who are done renting their firm from software
            vendors. Owner control over operations, compliance, people and growth.
          </p>
          <p className="overline mt-6 text-muted-foreground">beyondthebox.law</p>
        </div>

        <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 md:col-span-8">
          <div>
            <p className="overline text-muted-foreground">The stack</p>
            <ul className="mt-5 space-y-3">
              {productLinks.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    data-testid={`footer-link-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-foreground/80 transition-colors hover:text-accent"
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="overline text-muted-foreground">Resources</p>
            <ul className="mt-5 space-y-3">
              <li>
                <Link
                  to="/resources"
                  data-testid="footer-link-resources"
                  className="text-sm text-foreground/80 transition-colors hover:text-accent"
                >
                  Downloads
                </Link>
              </li>
              <li>
                <Link
                  to="/insights"
                  data-testid="footer-link-insights"
                  className="text-sm text-foreground/80 transition-colors hover:text-accent"
                >
                  Insights
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  data-testid="footer-link-faq"
                  className="text-sm text-foreground/80 transition-colors hover:text-accent"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/founder"
                  data-testid="footer-link-founder"
                  className="text-sm text-foreground/80 transition-colors hover:text-accent"
                >
                  Founder story
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="overline text-muted-foreground">Firm</p>
            <ul className="mt-5 space-y-3">
              <li>
                <Link to="/" className="text-sm text-foreground/80 transition-colors hover:text-accent">
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/book-a-walkthrough"
                  className="text-sm text-foreground/80 transition-colors hover:text-accent"
                >
                  Book a walkthrough
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </Container>

      <div className="border-t border-border">
        <Container className="flex flex-col gap-2 py-6 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <span>© {new Date().getFullYear()} Beyond the Box — a Bonsai Law company.</span>
          <span className="tracking-wide">Owner-grade legal infrastructure. No lock-in.</span>
        </Container>
      </div>
    </footer>
  );
}
